/** Minimal SDK call identity used to reconcile notification object updates. */
export interface VoiceCallIdentity {
  readonly id?: string;
  readonly callID?: string;
  readonly callId?: string;
}

export const getVoiceCallId = (call: VoiceCallIdentity): string | null =>
  call.callID ?? call.callId ?? call.id ?? null;

/** Match a fresh SDK Call object to the one the widget is already showing. */
export const callsMatch = (
  first: VoiceCallIdentity,
  second: VoiceCallIdentity,
): boolean => {
  const firstId = getVoiceCallId(first);
  const secondId = getVoiceCallId(second);

  return firstId != null && secondId != null
    ? firstId === secondId
    : first === second;
};

export const isCurrentCallUpdate = (
  currentCall: VoiceCallIdentity | null,
  updatedCall: VoiceCallIdentity,
): boolean => currentCall != null && callsMatch(currentCall, updatedCall);

const OUTBOUND_RINGING_STATES = new Set([
  "new",
  "trying",
  "requesting",
  "recovering",
  "ringing",
  "answering",
  "early",
]);

const INCOMING_DONE_STATES = new Set(["active", "hangup", "destroy", "purge"]);

const TERMINAL_CALL_STATES = new Set(["hangup", "destroy", "purge"]);

export const isTerminalCallState = (state: string): boolean =>
  TERMINAL_CALL_STATES.has(state);

export const getUpdatedActiveCallState = (
  currentCall: {
    readonly direction: "inbound" | "outbound";
    readonly state: string | null;
  },
  updatedState: string | null | undefined,
): string | null =>
  currentCall.direction === "outbound" &&
  currentCall.state === "ringing" &&
  updatedState != null &&
  OUTBOUND_RINGING_STATES.has(updatedState)
    ? "ringing"
    : (updatedState ?? null);

export const shouldClearIncomingCall = (
  incomingCall: VoiceCallIdentity,
  updatedCall: VoiceCallIdentity,
  updatedState: string,
): boolean =>
  INCOMING_DONE_STATES.has(updatedState) &&
  callsMatch(incomingCall, updatedCall);

/** True when this hangup belongs to the call the widget is presenting. */
export const shouldRestoreWidgetForTerminalCall = (
  incomingCall: VoiceCallIdentity | null,
  activeCall: VoiceCallIdentity | null,
  updatedCall: VoiceCallIdentity,
  updatedState: string,
): boolean =>
  isTerminalCallState(updatedState) &&
  ((incomingCall != null && callsMatch(incomingCall, updatedCall)) ||
    (activeCall != null && callsMatch(activeCall, updatedCall)));
